import { apiFetch } from "./client";

export type DlqEntrySummary = {
  readonly key: string;
  readonly origin_queue: string;
  readonly ulid: string;
  readonly partition_date: string;
  readonly message_id: string;
  readonly attempts: number;
  readonly enqueue_ms: number;
  readonly body_bytes: number;
  readonly last_error_class: string;
  readonly uploaded_at: string;
  readonly replayed_at: string | null;
  readonly replayed_by: string | null;
};

export type DlqEntryDetail = DlqEntrySummary & {
  readonly body: string;
};

export type FetchDlqEntriesInput = {
  readonly origin_queue: string;
  readonly from_date?: string;
  readonly to_date?: string;
  readonly cursor?: string;
  readonly limit: number;
};

export type FetchDlqEntriesResult = {
  readonly items: ReadonlyArray<DlqEntrySummary>;
  readonly total: number;
  readonly cursor: string | null;
};

export type FetchDlqDetailInput = {
  readonly origin_queue: string;
  readonly ulid: string;
  readonly partition_date: string;
};

export async function fetchDlqEntries(
  input: FetchDlqEntriesInput,
): Promise<FetchDlqEntriesResult> {
  const query = new URLSearchParams();
  query.set("limit", String(input.limit));
  appendOptional(query, "from_date", input.from_date);
  appendOptional(query, "to_date", input.to_date);
  appendOptional(query, "cursor", input.cursor);
  const path = `/admin/dlq/${encodeURIComponent(input.origin_queue)}?${query.toString()}`;
  const res = await apiFetch<{
    data: {
      readonly items: ReadonlyArray<DlqEntrySummary>;
      readonly total: number;
    };
    cursor?: string | null;
  }>(path);
  return {
    items: res.data.items,
    total: res.data.total,
    cursor: res.cursor ?? null,
  };
}

export async function fetchDlqDetail(
  input: FetchDlqDetailInput,
): Promise<DlqEntryDetail> {
  const query = new URLSearchParams();
  query.set("partition_date", input.partition_date);
  const path = `/admin/dlq/${encodeURIComponent(input.origin_queue)}/${encodeURIComponent(input.ulid)}?${query.toString()}`;
  const res = await apiFetch<{ data: DlqEntryDetail }>(path);
  return res.data;
}

export async function replayDlqEntry(
  input: FetchDlqDetailInput,
): Promise<DlqEntrySummary> {
  const query = new URLSearchParams();
  query.set("partition_date", input.partition_date);
  const path = `/admin/dlq/${encodeURIComponent(input.origin_queue)}/${encodeURIComponent(input.ulid)}/replay?${query.toString()}`;
  const res = await apiFetch<{ data: DlqEntrySummary }>(path, {
    method: "POST",
  });
  return res.data;
}

function appendOptional(
  query: URLSearchParams,
  key: string,
  value: string | undefined,
): void {
  if (value && value.trim().length > 0) query.set(key, value.trim());
}
